import { ChartOptions, Chart as ChartJS } from 'chart.js'
import dayjs from 'dayjs'

import { DatasetCategory } from 'src/commons/chartjs'
import { getTranslation } from 'src/commons/i18n'

import { GranularityEnum, IGenericBucket } from '../types'
import { formatNumber, IBaseBuildOptions } from '../utils'

enum LegendPosition {
  'bottom' = 'bottom',
  'left' = 'left',
  'right' = 'right',
  'top' = 'top',
}

export const STYLE_CHART_CONTAINER: Record<string, unknown> = {
  margin: 'auto',
}

export interface IGraphProps {
  datasetCategory?: DatasetCategory
  displayLegend?: boolean
  formatWithUnit?: (x: string) => string
  granularity?: GranularityEnum
  h?: number
  hiddenLabels?: string[]
  maxValue?: number
  rawData: any[]
  showTitle?: boolean
  title?: string
  w?: number
  xTicksFormatter?: (value: string) => string
  yTicksFormatter?: (num: number) => string
}

interface IOptions {
  displayLegend?: boolean
  enableAnimation: boolean
  legendPosition?: string
  showTitle?: boolean
  title?: string
}

export const getDefaultOptions = ({
  title,
  showTitle,
  displayLegend,
  legendPosition,
  enableAnimation,
}: IOptions): ChartOptions => {
  const options: ChartOptions = {
    animation: {
      duration: 0,
    },
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: displayLegend
        ? {
            display: true,
            position: legendPosition ? (legendPosition as LegendPosition) : 'bottom',
          }
        : {
            display: false,
          },
      title: {
        display: showTitle,
        text: title,
        font: { size: 18 },
      },
    },
  }

  if (enableAnimation) {
    options.animation = {
      easing: 'easeOutQuart',
      duration: 750,
    }
  }

  return options
}

const getTicks = (formatWithUnit: (x: string) => string, customFormatter?: (value: any) => string) => ({
  beginAtZero: true,
  callback: (label: string | number) => {
    if (customFormatter) {
      return customFormatter(label)
    }
    if (typeof label === 'number') {
      return formatWithUnit(formatNumber(label))
    }
    return formatWithUnit(label)
  },
})

export const getXTicks = () => ({
  padding: 5,
})

export const getYTicks = (formatWithUnit: (x: string) => string, customFormatter?: (value: any) => string) => ({
  autoSkip: false,
  maxTicksLimit: 6,
  ...getTicks(formatWithUnit, customFormatter),
})

export const getAdditionalLegend = (dataset: any, index: any, seperator = '|') =>
  dataset.additionalLegend && dataset.additionalLegend.length && dataset.additionalLegend[index]
    ? ` ${seperator} (${dataset.additionalLegend[index]})`
    : ''

const getFormat = (granularity: GranularityEnum) => {
  const { t } = getTranslation()
  switch (granularity) {
    case 'quarter':
      return t('date_format.quarter')
    case 'month':
      return t('date_format.month')
    case 'week':
      return t('date_format.week')
    case 'day':
      return t('date_format.day')
    case 'hour':
      return t('date_format.hour')
    default:
      return t('date_format.day')
  }
}

export function getLabelsWithGranularity(buckets: IGenericBucket[], granularity: GranularityEnum): string[] {
  return buckets.map((b) => {
    return dayjs(b.key).format(getFormat(granularity))
  })
}

interface IBuildHistogramOptions extends IBaseBuildOptions {
  adjacentBars?: boolean
  enableAnimation: boolean
  horizontal?: boolean
  maxValue?: number
}

export function buildHistogramOptions({
  title,
  showTitle,
  enableAnimation,
  maxValue = 0,
  formatWithUnit = (x: string) => x,
  adjacentBars,
  horizontal,
  displayLegend,
}: IBuildHistogramOptions): ChartOptions<'bar'> {
  const options: ChartOptions<'bar'> = {
    ...getDefaultOptions({ title, showTitle, displayLegend, enableAnimation }),
    indexAxis: horizontal ? 'y' : 'x',
    scales: {
      x: {
        max: maxValue > 0 ? maxValue : undefined,
        stacked: !adjacentBars,
        ticks: horizontal ? getYTicks(formatWithUnit) : getXTicks(),
      },
      y: {
        max: maxValue > 0 ? maxValue : undefined,
        stacked: !adjacentBars,
        ticks: horizontal ? getXTicks() : getYTicks(formatWithUnit),
      },
    },
    hover: {
      mode: 'index',
    },
  }
  options.plugins!.tooltip = {
    mode: 'index',
    callbacks: {
      label: function (tooltipItem: any) {
        const dataset = tooltipItem.dataset
        // Additional legend to display in tooltip, after the value
        const additionalLegend = getAdditionalLegend(dataset, tooltipItem.dataIndex)
        return `${dataset.label}: ${formatWithUnit(formatNumber(tooltipItem.raw))} ${additionalLegend}`
      },
    },
  }
  return options
}

export function getChartPlugins() {
  return [
    {
      id: 'custom_canvas_background_color',
      beforeDraw: (chart: ChartJS) => {
        const ctx = chart.canvas.getContext('2d')
        if (ctx) {
          ctx.save()
          ctx.globalCompositeOperation = 'destination-over'
          ctx.fillStyle = 'white'
          ctx.fillRect(0, 0, chart.canvas.width, chart.canvas.height)
          ctx.restore()
        }
      },
    },
  ]
}
